import { zodResolver } from "@hookform/resolvers/zod";
import { Mail } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForgotPasswordMutation } from "@/features/organizers/api";
import { ApiError } from "@/lib/api-client";
import { useT } from "@/lib/i18n";

const schema = z.object({
  email: z.string().email("validation.emailInvalid"),
});

type FormValues = z.infer<typeof schema>;

/**
 * Backend email kayıtlı olsun olmasın 204 döner; ekran da her durumda aynı
 * "gönderdik" mesajını gösterir ki hangi adresin kayıtlı olduğu anlaşılmasın.
 */
export function ForgotPasswordPage() {
  const t = useT("auth");
  const forgot = useForgotPasswordMutation();
  const [sentTo, setSentTo] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  async function onSubmit(values: FormValues) {
    try {
      await forgot.mutateAsync(values);
      setSentTo(values.email);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        toast.error(t("tooManyAttempts"));
        return;
      }
      toast.error(err instanceof ApiError ? err.detail : t("forgotPage.failed"));
    }
  }

  if (sentTo) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader className="space-y-4">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-7 w-7 text-primary" />
            </div>
            <div className="space-y-2">
              <CardTitle className="text-2xl">{t("forgotPage.sentTitle")}</CardTitle>
              <CardDescription>
                <Trans
                  i18nKey="auth.forgotPage.sentDescription"
                  values={{ email: sentTo }}
                  components={{ b: <strong /> }}
                />
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{t("forgotPage.sentHint")}</p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">{t("backToLogin")}</Link>
            </Button>
            <button
              type="button"
              onClick={() => setSentTo(null)}
              className="text-sm text-muted-foreground hover:text-foreground hover:underline"
            >
              {t("forgotPage.tryAnother")}
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="text-2xl">{t("forgotPage.title")}</CardTitle>
          <CardDescription>{t("forgotPage.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("email")}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="email"
                        placeholder={t("emailPlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={forgot.isPending}>
                {forgot.isPending ? t("forgotPage.submitting") : t("forgotPage.submit")}
              </Button>
            </form>
          </Form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {t("forgotPage.remembered")}{" "}
            <Link to="/login" className="font-medium text-foreground hover:underline">
              {t("forgotPage.login")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
